import { useState, useMemo, useEffect } from 'react';
import { refHandler } from 'shared/lib/helper-functions';
import { AddComment } from 'app/components/editor/sidebar/comments/AddComment';
import { AddTag } from 'app/components/editor/sidebar/tags/AddTag';
import { SeletedTextWrapper } from './Paragraph';
import { useEditorMode } from 'app/state/contexts/EditorContext';
import { useCoverageScope } from 'app/components/editor/coverage/CoverageScopeContext';
import { v4 as uuidv4 } from 'uuid';
import { useEditorCaret } from 'app/state/contexts/EditorUiContext';
import { RenderElementProps } from 'slate-react';

export const ListItemElement = ({
  element,
  attributes,
  children,
  withComments,
  withTags,
}: RenderElementProps & { withComments?: boolean; withTags?: boolean }) => {
  const { isTaggingMode } = useEditorMode();
  const [ref, setRef] = useState<HTMLElement | null>(null);
  const [show, setShow] = useState(false);
  const selectionId = useEditorCaret().selectionId;
  const el = element as { uuid?: string; formatting?: { class?: string } };
  const elementUUID = useMemo(() => el.uuid || uuidv4(), [el.uuid]);
  const anchor = `anchor-${elementUUID}`;
  const listId = `xrp-list-${elementUUID}`;

  const safeAttributesWithId = (attrs: RenderElementProps['attributes']) => ({
    ...attrs,
    id: (attrs as { id?: string }).id || listId,
  });

  useEffect(() => {
    if (!selectionId) {
      setShow(false);
      return;
    }

    setShow(selectionId === element.uuid);
  }, [element.uuid, selectionId]);

  const { scopeUuids, missingUuids } = useCoverageScope();
  let coverageClass = '';
  if (element.uuid) {
    if (missingUuids.has(element.uuid)) coverageClass = ' coverage-missing';
    else if (scopeUuids.has(element.uuid)) coverageClass = ' coverage-scope';
  }

  const liAttrs = safeAttributesWithId(attributes);
  const liClassName = `${[(liAttrs as { className?: string }).className, el.formatting?.class].filter(Boolean).join(' ')}${coverageClass}`.trim() || undefined;
  return (
    <li {...liAttrs} className={liClassName} ref={refHandler(attributes, (node) => setRef(node as HTMLElement | null))}>
      <a contentEditable="false" href={`#${anchor}`} id={anchor}></a>
      <SeletedTextWrapper popperRef={ref} show={show}>
        <>
          {!!withComments && !isTaggingMode && ref ? <AddComment element={element} /> : null}
          {withTags && isTaggingMode && ref ? <AddTag element={element as { uuid: string }} /> : null}
        </>
      </SeletedTextWrapper>
      {children}
    </li>
  );
};
